import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface CmsContent {
  id: string;
  section_id: string;
  key: string;
  value: any;
  type: 'text' | 'number' | 'image' | 'html' | 'counter';
  label: string;
  created_at: string;
  updated_at: string;
}

interface UseCmsContentOptions {
  section?: string;
  key?: string;
  refreshInterval?: number;
}

export function useCmsContent(options: UseCmsContentOptions = {}) {
  const [content, setContent] = useState<CmsContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    fetchContent();
    
    // Imposta un intervallo di aggiornamento se specificato
    if (options.refreshInterval) {
      const intervalId = setInterval(() => {
        fetchContent();
      }, options.refreshInterval);
      
      return () => clearInterval(intervalId);
    }
  }, [options.section, options.key, options.refreshInterval]);

  const fetchContent = async () => {
    setLoading(true);
    setError(null);

    try {
      let query = supabase.from('cms_content').select(`
        *,
        cms_sections(*)
      `);

      // Se è specificata una sezione, filtra per slug della sezione
      if (options.section) {
        query = query.eq('cms_sections.slug', options.section);
      }

      // Se è specificata una chiave, filtra per chiave
      if (options.key) {
        query = query.eq('key', options.key);
      }

      const { data, error } = await query;

      if (error) throw error;
      setContent(data || []);
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error('Error fetching CMS content:', err);
      setError(err.message || 'Errore nel caricamento dei contenuti');
    } finally {
      setLoading(false);
    }
  };

  const updateContent = async (id: string, value: any) => {
    try {
      const { error } = await supabase
        .from('cms_content')
        .update({ value })
        .eq('id', id);

      if (error) throw error;

      // Aggiorna lo stato locale
      setContent(prev => 
        prev.map(item => 
          item.id === id ? { ...item, value } : item
        )
      );
      setLastUpdated(new Date());

      return { success: true };
    } catch (err: any) {
      console.error('Error updating CMS content:', err);
      return { success: false, error: err.message };
    }
  };

  // Helper per ottenere il valore di un contenuto specifico per chiave
  const getContentValue = (key: string) => {
    const item = content.find(c => c.key === key);
    if (!item) return null;
    
    try {
      // Se il valore è una stringa JSON, la analizziamo
      if (typeof item.value === 'string' && 
          (item.value.startsWith('"') || item.value.startsWith('{'))) {
        return JSON.parse(item.value);
      }
      return item.value;
    } catch {
      // Se l'analisi fallisce, restituiamo il valore originale
      return item.value;
    }
  };

  // Helper per il formato dei contatori
  const getCounterValue = (key: string, defaultValue: number = 0) => {
    const value = getContentValue(key);
    if (value === null) return defaultValue;
    
    // Se è un numero restituiscilo
    if (typeof value === 'number') return value;
    
    // Se è una stringa, prova a convertirla in numero
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? defaultValue : parsed;
    }
    
    return defaultValue;
  };

  return {
    content,
    loading,
    error,
    updateContent,
    getContentValue,
    getCounterValue,
    refetch: fetchContent,
    lastUpdated
  };
}